const SERVER_TIMEZONE_OFFSET_MS = 8 * 60 * 60 * 1000;
const LEGACY_DATETIME_PATTERN = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})[ T](\d{1,2}):(\d{1,2})(?::(\d{1,2}))?$/;

const pad = (value) => String(value).padStart(2, '0');

const isValidDate = (date) => date instanceof Date && !Number.isNaN(date.getTime());

/**
 * Parses server timestamps as real instants. Legacy strings without an offset
 * are stored and emitted in the application's Asia/Shanghai business timezone.
 */
export const parseDateTime = (value) => {
	if (value instanceof Date) {
		return isValidDate(value) ? new Date(value.getTime()) : null;
	}

	if (value === null || value === undefined || value === '') return null;

	if (typeof value === 'number') {
		if (!Number.isFinite(value) || value <= 0) return null;
		const milliseconds = Math.abs(value) >= 100000000000 ? value : value * 1000;
		const date = new Date(milliseconds);
		return isValidDate(date) ? date : null;
	}

	if (typeof value !== 'string') return null;

	const normalizedValue = value.trim();
	if (!normalizedValue || /^0{4}[-/]0{2}[-/]0{2}/.test(normalizedValue)) return null;

	if (/^\d+(?:\.\d+)?$/.test(normalizedValue)) {
		return parseDateTime(Number(normalizedValue));
	}

	const legacyMatch = normalizedValue.match(LEGACY_DATETIME_PATTERN);
	if (legacyMatch) {
		const [, year, month, day, hour, minute, second = '0'] = legacyMatch;
		const utcMilliseconds = Date.UTC(
			Number(year),
			Number(month) - 1,
			Number(day),
			Number(hour),
			Number(minute),
			Number(second)
		);
		const utcDate = new Date(utcMilliseconds);
		if (
			utcDate.getUTCFullYear() !== Number(year) ||
			utcDate.getUTCMonth() !== Number(month) - 1 ||
			utcDate.getUTCDate() !== Number(day) ||
			utcDate.getUTCHours() !== Number(hour) ||
			utcDate.getUTCMinutes() !== Number(minute) ||
			utcDate.getUTCSeconds() !== Number(second)
		) {
			return null;
		}

		return new Date(utcMilliseconds - SERVER_TIMEZONE_OFFSET_MS);
	}

	const parsedMilliseconds = Date.parse(normalizedValue);
	if (Number.isNaN(parsedMilliseconds)) return null;

	return new Date(parsedMilliseconds);
};

/**
 * Formats a timestamp in the browser's local timezone while preserving the
 * application's existing YYYY-MM-DD HH:mm:ss presentation.
 */
export const formatBrowserTime = (value, fallback = '') => {
	const date = parseDateTime(value);
	if (!date) return fallback;

	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};
